import { DGEImportLicensePreview } from '@img-arena/consoles-api-streaming'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { GeoRegion, territories } from '@/components/forms/GeoBlockingTemplateForm'
import { CollapsibleRegionChips } from '@/components/inputs/common/CollapsibleRegionChips'
import { LicensePropertyAutocomplete } from '@/components/inputs/LicensePropertyAutocomplete'
import { DGELicensingPeriod } from '@/components/inputs/LicensingPeriods/streaming/LicensingPeriod.dge'

import { UpdatedPeriod } from './types'

export type ImportedLicenseAccordionProps = {
  license: DGEImportLicensePreview
  index: number
  isSelected: boolean
  isExpanded: boolean
  expandedPeriods: Set<string>
  selectedProperty?: number
  selectedPropertyName?: string
  excludedProperties: number[]
  onSelectionChange: (index: number, selected: boolean) => void
  onAccordionChange: (index: number, expanded: boolean) => void
  onAutoBookingChange: (index: number, autoBooking: boolean) => void
  onPeriodExpandedChange: (licenseIndex: number, periodIndex: number, expanded: boolean) => void
  onPeriodChange: (licenseIndex: number, periodIndex: number, updatedPeriod: UpdatedPeriod) => void
  onDeletePeriod: (licenseIndex: number, periodIndex: number) => void
  onPropertyChange: (licenseIndex: number, propertyId: number | null) => void
}

export const ImportedLicenseAccordion = ({
  license,
  index,
  isSelected,
  isExpanded,
  expandedPeriods,
  selectedProperty,
  selectedPropertyName,
  excludedProperties,
  onSelectionChange,
  onAccordionChange,
  onAutoBookingChange,
  onPeriodExpandedChange,
  onPeriodChange,
  onDeletePeriod,
  onPropertyChange
}: ImportedLicenseAccordionProps) => {
  const convertCountryCodesToGeoRegions = (countryCodes: string[]): GeoRegion[] => {
    return countryCodes
      .map((code: string) => territories.find((territory) => territory.iso2 === code))
      .filter((region: GeoRegion | undefined): region is GeoRegion => region !== undefined)
  }

  const hasOriginalProperty = license.property?.id && license.property?.name
  const hasSelectedProperty = selectedProperty !== undefined
  const isPropertyValid = hasOriginalProperty || hasSelectedProperty

  const MIN_YEAR = 2010
  const MAX_YEAR = 2050
  const hasValidYears =
    license.licenseDates?.every((period) => {
      const startYear = new Date(period.startDate).getFullYear()
      const endYear = new Date(period.endDate).getFullYear()
      return (
        startYear >= MIN_YEAR && startYear <= MAX_YEAR && endYear >= MIN_YEAR && endYear <= MAX_YEAR
      )
    }) ?? true

  const isLicenseValid = isPropertyValid && hasValidYears

  return (
    <Accordion
      key={index}
      expanded={isExpanded}
      onChange={(_, expanded) => onAccordionChange(index, expanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel${index}bh-content`}
        id={`panel${index}bh-header`}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: '100%' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title={!isLicenseValid && 'License has validation errors'} arrow>
            <Box>
              <Checkbox
                checked={isSelected}
                disabled={!isLicenseValid}
                onChange={(e) => {
                  e.stopPropagation()
                  onSelectionChange(index, e.target.checked)
                }}
                onClick={(e) => e.stopPropagation()}
                size="small"
              />
            </Box>
          </Tooltip>
          {hasOriginalProperty ? (
            <Typography fontWeight="bold">{license.property?.name}</Typography>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
              <Box sx={{ width: '50%' }}>
                <LicensePropertyAutocomplete.DGE
                  onChange={(value) => {
                    onPropertyChange(index, value)
                  }}
                  value={selectedProperty}
                  exclude={excludedProperties}
                />
              </Box>
            </Stack>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack>
          {!isLicenseValid && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {!isPropertyValid && !hasValidYears
                ? 'This license has missing property data and invalid years (must be between 2010-2050)'
                : !isPropertyValid
                  ? 'This license has missing property data'
                  : 'This license has invalid years (must be between 2010-2050)'}
            </Alert>
          )}

          <Stack flexDirection="row" gap={0.5} alignItems="center">
            <Typography fontSize={12} fontWeight="bold">
              License property:
            </Typography>
            <Typography fontSize={12} color={isPropertyValid ? 'inherit' : 'error'}>
              {hasOriginalProperty
                ? license.property?.name
                : hasSelectedProperty
                  ? selectedPropertyName || 'Property Selected'
                  : 'Missing property'}
            </Typography>
          </Stack>

          {license.geoList?.countryCodes && (
            <CollapsibleRegionChips
              regions={convertCountryCodesToGeoRegions(license.geoList.countryCodes)}
              isWhiteList={license.geoList.isWhitelist || false}
              maxVisible={5}
            />
          )}

          <Stack gap={1}>
            <Stack
              flexDirection="row"
              gap={0.5}
              alignItems="center"
              width="100%"
              justifyContent="space-between"
            >
              <Typography fontSize={12} fontWeight="bold">
                License Periods:
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={license.isAutomaticBooking || false}
                    onChange={(e) => onAutoBookingChange(index, e.target.checked)}
                    size="small"
                  />
                }
                sx={{ mr: 0 }}
                label={<Typography fontSize={12}>Allow auto-booking</Typography>}
              />
            </Stack>
            <Stack spacing={1}>
              {license.licenseDates?.map((period, periodIndex) => {
                const periodData = {
                  year: new Date(period.startDate).getFullYear().toString(),
                  startDate: period.startDate,
                  endDate: period.endDate,
                  maxEvents: period.maxEvents,
                  purchasedEvents: period.purchasedEvents
                }

                const periodId = `license-${index}-period-${periodIndex}`
                const isExpanded = expandedPeriods.has(periodId)

                return (
                  <DGELicensingPeriod
                    key={periodIndex}
                    value={{
                      ...periodData,
                      maxEvents: periodData.maxEvents ?? undefined,
                      purchasedEvents: periodData.purchasedEvents ?? undefined
                    }}
                    expanded={isExpanded}
                    usedYears={new Set()}
                    onExpandedChange={(expanded) =>
                      onPeriodExpandedChange(index, periodIndex, expanded)
                    }
                    onDelete={() => onDeletePeriod(index, periodIndex)}
                    onChange={(updatedPeriod) => onPeriodChange(index, periodIndex, updatedPeriod)}
                    autoBooking={license.isAutomaticBooking || false}
                    hasError={false}
                  />
                )
              })}
            </Stack>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}
